/**
 * Simulates an AI text summarization service.
 */

/**
 * Asynchronously summarizes a given text.
 * This is a mock implementation that simulates an API call.
 *
 * @param text The text to be summarized.
 * @returns A Promise that resolves to the summarized text.
 * @throws An error if the simulated AI service fails.
 */
export const summarizeText = async (text: string): Promise<string> => {
  console.log("Original text for summarization:", text);

  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1000)); // 1-second delay

  // Simulate Random Error (Optional - uncomment for testing error states)
  /*
  if (Math.random() < 0.2) { // 20% chance of error
    console.error("Simulated AI Summarization Error");
    throw new Error("Simulated AI service is unavailable at the moment.");
  }
  */

  if (!text || text.trim().length === 0) {
    return "[Summary could not be generated for empty text]";
  }

  if (text.length < 50) {
    return `[Summary could not be generated for very short text]: ${text}`;
  }

  // Mock Summarization Logic
  const sentences = text.split(/[.!?]/); // Split by common sentence terminators

  let firstMeaningfulSentence = "";
  for (const sentence of sentences) {
    if (sentence.trim().length > 20) { // Consider a sentence meaningful if it's longer than 20 chars
      firstMeaningfulSentence = sentence.trim();
      break;
    }
  }

  if (firstMeaningfulSentence) {
    return `${firstMeaningfulSentence}. (AI Summary)`;
  } else {
    // Fallback if no sentence is longer than 20 chars, or only one short sentence part exists
    const shortSummary = text.substring(0, Math.min(text.length, 150));
    return `${shortSummary.trim()}${text.length > 150 ? '...' : ''} (AI Summary)`;
  }
};
